import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Avatar } from '@chakra-ui/react';
import {
  Badge,
  Box,
  Button,
  Container,
  HStack,
  Input,
  SimpleGrid,
  Text,
  VStack,
  useBreakpointValue,
  Tooltip,
  Image
} from "@chakra-ui/react";

function Login() {
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [selectedRoomId, setSelectedRoomId] = useState(-1);
  const [selectedRoomName, setSelectedRoomName] = useState(null);

  // 你原本的三個房間，補上一點屬性做展示
  const chatRooms = useMemo(
    () => [
      { id: 0, label: "喵喵基地", type: "公開", icon: "/assets/rooms/cat.png" },
      { id: 1, label: "塔樓秘境", type: "公開", icon: "/assets/rooms/tower.png" }, // 要做密語可把 type 改成 "密語"
      { id: 2, label: "狗狗星球", type: "公開", icon: "/assets/rooms/dog.png" },
      { id: 3, label: "企鵝王國", type: "公開", icon: "/assets/rooms/penguin.png" }
    ],
    []
  );

  const handleName = (e) => setName(e.target.value);

  const handleRoomSelect = (roomId, roomName) => {
    setSelectedRoomId(roomId);
    setSelectedRoomName(roomName);
  };

  const handleLogin = () => {
    if (!name.trim() || selectedRoomId < 0) return;
    // 確保中文/Emoji 安全帶入 URL
    const qs = new URLSearchParams({
      name: name.trim(),
      roomId: String(selectedRoomId),
      roomName: selectedRoomName ?? "",
    });
    navigate(`/ws?${qs.toString()}`);
  };

  // 手機：把卡片做得稍微大一點
  const cardHeight = useBreakpointValue({ base: "200px", md: "180px" });

  // 讓輸入或按鈕在切換頁面狀態時更易用
  const nameInputRef = useRef(null);
  const enterBtnRef = useRef(null);
  useEffect(() => {
    if (selectedRoomId >= 0 && nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, [selectedRoomId]);
  useEffect(() => {
    if (selectedRoomId >= 0 && name.trim() && enterBtnRef.current) {
      enterBtnRef.current.focus();
    }
  }, [selectedRoomId]);

  const ChatRoomCard = ({ room, selected, onSelect }) => {
    const { label, type, icon } = room; // icon: '/assets/rooms/cat.png' 之類

    return (
      <Box
        as="button"
        onClick={onSelect}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onSelect();
          }
        }}
        tabIndex={0}
        aria-pressed={selected}
        role="group"
        h={{ base: 140, md: 160 }}
        w="100%"
        borderRadius="2xl"
        px={6}
        py={5}
        bg="whiteAlpha.600"
        backdropFilter="blur(16px)"
        border="1px solid"
        borderColor={selected ? "purple.300" : "whiteAlpha.400"}
        boxShadow={selected ? "0 8px 28px rgba(0,0,0,0.14)" : "0 4px 16px rgba(0,0,0,0.08)"}
        transition="all 220ms ease"
        _hover={{
          transform: "translateY(-4px)",
          boxShadow: "0 10px 30px rgba(0,0,0,0.16)",
          borderColor: "rgba(168,85,247,0.36)",
        }}
        _active={{ transform: "translateY(-1px) scale(0.995)" }}
        _focusVisible={{ boxShadow: "0 0 0 3px rgba(167,139,250,0.35)" }}
        // 讓內容真正「置中」
        display="flex"
        flexDir="column"
        alignItems="center"
        justifyContent="center"
        textAlign="center"
        position="relative"
        outline="none"
      >
        {/* 右上角角標 */}
        {selected && (
          <Box
            position="absolute"
            top={2}
            right={2}
            fontSize="xs"
            bg="purple.500"
            color="white"
            px={2}
            py={0.5}
            borderRadius="md"
          >
            ✓ 已選取
          </Box>
        )}

        <VStack spacing={2} align="center" w="full">
          {/* Avatar：移除舊的漸層圓徽章，避免紫色半月 */}
          <Box
            w="56px"
            h="56px"
            borderRadius="full"
            overflow="hidden"
            boxShadow="inner"
            border="2px solid"
            borderColor="white"
            transform={selected ? "scale(1.06)" : undefined}
            _groupHover={{ transform: "scale(1.06)" }}
            transition="200ms"
          >
            <Image
              src={icon}       // e.g. '/assets/rooms/cat.png'
              alt={label}
              w="100%"
              h="100%"
              objectFit="cover"
              draggable={false}
            />
          </Box>

          {/* 標題：避免被吃字，並提供 Tooltip */}
          <Tooltip label={label} hasArrow openDelay={400}>
            <Text fontWeight="semibold" color="gray.800" noOfLines={1} maxW="80%">
              {label}
            </Text>
          </Tooltip>

          {/* 副標 & 徽章 */}
          <HStack spacing={2} opacity={0.85} fontSize="xs" justify="center">
            <Text>點擊選擇</Text>
            <Badge colorScheme={type === "公開" ? "purple" : "gray"} variant="subtle">
              {type}
            </Badge>
          </HStack>
        </VStack>
      </Box>
    );
  };

  return (
     <>
     <Helmet>
        <title>EchoMind</title>
        <meta
          name="description"
          content="這是一個支援 AI 對話與多人互動的即時聊天室"
        />
        <meta property="og:title" content="AI 即時聊天室" />
        <meta property="og:description" content="與朋友或 AI 聊天，體驗智慧對話。" />
      </Helmet>
    <Box position="relative" minH="100svh" bg="transparent">
      {/* 背景層（柔光 + 斜向漸層 + 細微噪點感） */}
      <Box
        position="absolute"
        inset={0}
        zIndex={0}
        pointerEvents="none"
        _before={{
          content: '""',
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(135deg, rgba(155,125,255,0.18), rgba(255,150,180,0.18))",
        }}
        _after={{
          content: '""',
          position: "absolute",
          inset: 0,
          background: `
            radial-gradient(
              900px 900px at 15% 8%,
              rgba(204,156,248,0.30) 0%,
              rgba(204,156,248,0.16) 40%,
              transparent 80%
            ),
            radial-gradient(
              900px 900px at 85% 82%,
              rgba(230,130,180,0.30) 0%,
              rgba(230,130,180,0.14) 40%,
              transparent 80%
            )
          `,
        }}
      />

      {/* 內容層 */}
      <Container
        maxW="6xl"
        py={{ base: 10, md: 14 }}
        minH="70vh"
        position="relative"
        zIndex={1}
        display="grid"
        placeItems="center"
      >
        {selectedRoomId === -1 ? (
          // 選擇聊天室
          <VStack spacing={6} w="full">
            <Text
              textAlign="center"
              fontSize={{ base: "xl", md: "2xl" }}
              fontWeight="bold"
              mb={2}
              color="gray.800"
              letterSpacing="0.02em"
            >
              選擇一個聊天室
            </Text>
            <Text
              fontSize="sm"
              color="gray.600"
              textAlign="center"
              mb={2}
            >
              點選下方任一張卡片以選擇聊天室
            </Text>

            <SimpleGrid
              w="full"
              columns={{ base: 1, sm: 2, md: 2 }}
              spacing={{ base: 4, md: 12 }}
            >
              {chatRooms.map((room) => (
                <ChatRoomCard
                  key={room.id}
                  room={room}
                  selected={selectedRoomId === room.id}
                  onSelect={() => handleRoomSelect(room.id, room.label)}
                />
              ))}
            </SimpleGrid>

            {/* 二次動作提示 */}
            <Text fontSize="xs" color="gray.500" mt={1}>
              已選取卡片後，將出現暱稱輸入與進入按鈕
            </Text>
          </VStack>
        ) : (
          // 輸入暱稱並進入聊天室
          <VStack spacing={6} w="full" maxW="720px" mx="auto">
            <Text
              fontSize={{ base: "xl", md: "2xl" }}
              fontWeight="bold"
              textAlign="center"
              color="gray.800"
            >
              進入 {selectedRoomName} 聊天室
            </Text>

            <Input
              ref={nameInputRef}
              name="name"
              placeholder="請輸入暱稱"
              size="lg"
              width="100%"
              bg="whiteAlpha.300"
              color="purple.700"
              borderColor="whiteAlpha.600"
              _focus={{
                bg: "white",
                borderColor: "purple.300",
                boxShadow: "0 0 0 3px rgba(167,139,250,0.35)",
              }}
              value={name}
              onChange={handleName}
              onKeyDown={(e) => {
                if (e.key === "Enter" && name.trim().length) handleLogin();
              }}
            />

            <Button
              ref={enterBtnRef}
              colorScheme="purple"
              size="lg"
              type="button"
              onClick={handleLogin}
              isDisabled={!name.trim().length}
              rightIcon={<span>→</span>}
            >
              進入聊天室
            </Button>

            <Button
              variant="ghost"
              onClick={() => {
                setSelectedRoomId(-1);
                setSelectedRoomName(null);
              }}
            >
              ← 重新選擇聊天室
            </Button>
          </VStack>
        )}
      </Container>
    </Box>
    </>
  );
}

export default Login;
